// Phase 8.8 — Affiliate Payout Destinations
// Allow user to connect Stripe Connect / PayPal / Cash App / Bank.
// No funds are transferred — this only registers the destination + last4.

import React, { useEffect, useState } from "react";
import { Wallet, Save, Loader2, CheckCircle2, AlertCircle, Trash2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const KINDS = [
  { value: "stripe_connect", label: "Stripe Connect", placeholder: "Stripe Connect account id (acct_…)", hint: "Recommended — fastest payouts." },
  { value: "paypal",         label: "PayPal",         placeholder: "you@email.com",                   hint: "Send to a verified PayPal account." },
  { value: "cash_app",       label: "Cash App",       placeholder: "$YourCashtag",                    hint: "We pay to your Cashtag." },
  { value: "bank",           label: "Bank account",   placeholder: "ACH routing • last 4 of account", hint: "ACH transfer — 2-3 business days." },
];

export default function AffiliatePayouts() {
  const [kind, setKind] = useState("stripe_connect");
  const [handle, setHandle] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [notes, setNotes] = useState("");
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/payouts/destinations");
      if (data?.kind) {
        setSaved(data);
        setKind(data.kind);
        setDisplayName(data.display_name || "");
        setNotes(data.notes || "");
        setHandle(""); // never echo the full handle back; show last4 instead
      }
    } catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { load(); }, []);

  const cfg = KINDS.find((k) => k.value === kind);

  const save = async () => {
    if (!handle.trim()) { setErr("Please enter your payout handle."); return; }
    setSaving(true); setErr("");
    try {
      const { data } = await api.put("/payouts/destinations", {
        kind, handle: handle.trim(),
        display_name: displayName || undefined,
        notes: notes || undefined,
      });
      setSaved(data);
      setHandle("");
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirm("Remove this payout destination?")) return;  // eslint-disable-line no-restricted-globals, no-alert
    try {
      await api.delete("/payouts/destinations");
      setSaved(null); setHandle(""); setDisplayName(""); setNotes("");
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="affiliate-payouts">
      <header>
        <div className="eyebrow">Affiliate · Payouts</div>
        <h1 className="font-anton text-3xl uppercase">Payout destination</h1>
        <p className="text-lifted-dim text-sm mt-1">
          Where we send your commissions. You can change this any time. Funds are not transferred automatically yet —
          our payouts team reviews each destination before activating it.
        </p>
      </header>

      {/* Current destination */}
      {saved && (
        <div className="glass-panel p-4 flex items-start gap-3" data-testid="payout-current">
          <div className="h-10 w-10 rounded-md bg-lifted-green/15 text-lifted-green grid place-items-center shrink-0">
            <Wallet className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="text-sm text-lifted-text">
              {KINDS.find((k) => k.value === saved.kind)?.label || saved.kind}
              <span className="text-lifted-dim"> · ends in {saved.last4}</span>
            </div>
            {saved.display_name && <div className="text-xs text-lifted-dim">{saved.display_name}</div>}
            <div className="flex items-center gap-2 mt-1.5 text-[0.65rem]">
              {saved.status === "approved" ? (
                <span className="inline-flex items-center gap-1 text-lifted-green"><CheckCircle2 className="h-3 w-3" /> Approved</span>
              ) : saved.status === "rejected" ? (
                <span className="inline-flex items-center gap-1 text-rose-300"><AlertCircle className="h-3 w-3" /> Rejected{saved.rejection_reason ? ` — ${saved.rejection_reason}` : ""}</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-300"><AlertCircle className="h-3 w-3" /> Pending review</span>
              )}
            </div>
          </div>
          <button onClick={remove} className="text-rose-300 hover:text-rose-200 text-xs inline-flex items-center gap-1"
                  data-testid="payout-remove">
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
        </div>
      )}

      {/* Editor */}
      <div className="glass-panel p-5 space-y-4" data-testid="payout-editor">
        <div>
          <div className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-dim mb-2">METHOD</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {KINDS.map((k) => (
              <button key={k.value} onClick={() => setKind(k.value)}
                      className={`px-3 py-2 rounded-md border text-xs font-bebas tracking-[0.18em] uppercase ${
                        kind === k.value ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40" : "border-white/10 text-lifted-dim hover:text-white"
                      }`}
                      data-testid={`payout-kind-${k.value}`}>
                {k.label}
              </button>
            ))}
          </div>
        </div>

        <Field label={cfg?.label || "Handle"} value={handle} onChange={setHandle}
               placeholder={cfg?.placeholder} hint={cfg?.hint} testid="payout-handle" />

        <Field label="Display label (optional)" value={displayName} onChange={setDisplayName}
               placeholder="e.g. Main bank account" testid="payout-display-name" />

        <Field label="Internal notes (optional)" value={notes} onChange={setNotes}
               placeholder="Anything our payouts team should know"
               multiline testid="payout-notes" />

        {err && <div className="text-xs text-rose-300" data-testid="payout-error">{err}</div>}

        <button onClick={save} disabled={saving || !handle.trim()}
                className="btn-lifted !py-2 !px-4 text-xs inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="payout-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save destination
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, hint, multiline, testid }) {
  return (
    <div>
      <div className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-dim mb-1.5">{label}</div>
      {multiline ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)}
                  placeholder={placeholder} rows={2}
                  className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/40 focus:outline-none"
                  data-testid={testid} />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)}
               placeholder={placeholder}
               className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/40 focus:outline-none"
               data-testid={testid} />
      )}
      {hint && <div className="text-[0.65rem] text-lifted-dim mt-1">{hint}</div>}
    </div>
  );
}
